import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, EyeOff, LogOut } from 'lucide-react';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Separator } from '../components/Separator';
import { InternalGate } from '../components/internal/InternalGate';
import { HostBlocked } from '../components/internal/HostBlocked';
import { DataSourceBar } from '../components/internal/DataSourceBar';
import { NoEventsYet } from '../components/internal/NoEventsYet';
import { NotConnected } from '../components/internal/NotConnected';
import { TrafficSections } from '../components/internal/TrafficSections';
import { useInternalAccess } from '../hooks/useInternalAccess';
import { useNoIndex } from '../hooks/useNoIndex';
import { useTrafficData } from '../hooks/useTrafficData';
import { currentHost, isInternalHostAllowed } from '../utils/host';
import { supabaseConfig } from '../data/supabaseConfig';
export function InternalTraffic() {
  useNoIndex();
  const {
    isUnlocked,
    unlock,
    lock
  } = useInternalAccess();
  const {
    payload,
    status,
    error,
    lastUpdated,
    refresh
  } = useTrafficData();
  const host = currentHost();
  if (!isInternalHostAllowed(host)) return <HostBlocked host={host} data-id="element-637" data-mp="3d7ybjygr5p03" />;
  if (!isUnlocked) return <InternalGate onUnlock={unlock} data-id="element-638" data-mp="3d75fpsjc0jwq" />;
  return <main className="min-h-[100dvh] w-full bg-background px-4 py-8 sm:px-6 sm:py-10" data-id="element-639" data-mp="3i603l8w5oynp">
      <div className="mx-auto w-full max-w-6xl" data-id="element-640" data-mp="33hvkw4x3sn4x">
        <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between" data-id="element-641" data-mp="33g2fsu252az1">
          <div data-id="element-642" data-mp="389ws3cfezno3">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2" data-id="element-643" data-mp="38aey30rvpg97">
              <h1 className="font-heading text-xl font-semibold tracking-tight text-foreground sm:text-2xl" data-id="element-644" data-mp="2ui8ltprujtf1">
                Maintenance traffic
              </h1>
              <Badge variant="secondary" className="gap-1.5" data-id="element-645" data-mp="2ki7wnn7aqj2x">
                <EyeOff className="h-3 w-3" aria-hidden="true" data-id="element-646" data-mp="2kkucsb8966wr" />
                Internal only
              </Badge>
            </div>
            <p className="mt-2 text-sm text-muted-foreground" data-id="element-647" data-mp="v25lz1p2j8xk">
              Requests reaching the 503 page on {supabaseConfig.monitoredHost} during the current
              window · 20-minute buckets · all times UTC
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2" data-id="element-648" data-mp="m9k5xtctngcf">
            <Button variant="outline" onClick={() => window.history.back()} data-id="element-649" data-mp="hb0jlrubg2jj">
              <ArrowLeft className="h-4 w-4" aria-hidden="true" data-id="element-650" data-mp="1e086b36o8hp1" />
              Back
            </Button>
            <Button variant="ghost" onClick={lock} data-id="element-651" data-mp="19z66ak4xk1m0">
              <LogOut className="h-4 w-4" aria-hidden="true" data-id="element-652" data-mp="19zo7lv6g960s" />
              Lock
            </Button>
          </div>
        </header>

        <Separator className="my-6 sm:my-8" data-id="element-653" data-mp="100p6qyrxeeoc" />

        <DataSourceBar status={status} error={error} lastUpdated={lastUpdated} onRefresh={refresh} data-id="element-654" data-mp="zzxdvvppqv1r" />

        {status === 'not-configured' ? <NotConnected data-id="element-655" data-mp="3kq69vn88t17f" /> : payload === null ? <p className="mt-8 text-center font-mono text-xs text-muted-foreground" data-id="element-656" data-mp="3an9c8pzzc581">
            {status === 'error' ? 'No data to display — the query above failed.' : `Loading traffic for ${supabaseConfig.monitoredHost}…`}
          </p> : payload.series.length === 0 ? <NoEventsYet data-id="element-657" data-mp="3pbnfv357zxpn" /> : <TrafficSections payload={payload} data-id="element-658" data-mp="3pcoxxrknlkgv" />}

        <footer className="mt-8 space-y-1 text-xs text-muted-foreground" data-id="element-659" data-mp="3tdqzif21m8m0">
          <p data-id="element-660" data-mp="3tfv2mus4b3l8">
            Internal telemetry — do not share externally.{' '}
            <Link to="/" className="underline underline-offset-4 hover:text-foreground" data-id="element-661" data-mp="25cvyzep1m92">
              View the public maintenance page
            </Link>
          </p>
          <p className="font-mono" data-id="element-662" data-mp="c4c66mf3xpy6">
            Hosted on Magic Patterns ({host}) — reads {supabaseConfig.monitoredHost} traffic, never
            served from it
          </p>
        </footer>
      </div>
    </main>;
}